import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import ReactDOM from 'react-dom';

const InfoTooltip = ({ text }) => {
    const [showText, setShowText] = useState(false);
    const [position, setPosition] = useState('right');
    const [triggerRect, setTriggerRect] = useState(null);
    const tooltipRef = useRef(null);
    const triggerRef = useRef(null);

    const toggleText = (e) => {
        e.stopPropagation();
        const rect = triggerRef.current.getBoundingClientRect();
        setTriggerRect(rect);
        setShowText(!showText);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
                setShowText(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (showText && triggerRef.current) {
            const triggerElement = triggerRef.current;
            const triggerRect = triggerElement.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const container = findScrollContainer(triggerElement);
            const containerRect = container?.getBoundingClientRect() || { right: viewportWidth };

            const safetyMargin = 20;
            const tooltipWidth = 320;

            const spaceRight = containerRect.right - triggerRect.right - safetyMargin;
            const spaceLeft = triggerRect.left - (containerRect.left || 0) - safetyMargin;

            if (spaceRight < tooltipWidth && spaceLeft > spaceRight) {
                setPosition('left');
            } else {
                setPosition('right');
            }
        }
    }, [showText]);

    const findScrollContainer = (element) => {
        while (element.parentElement) {
            element = element.parentElement;
            if (element.scrollWidth > element.clientWidth) {
                return element;
            }
        }
        return null;
    };

    return (
        <span className="relative inline-flex" ref={tooltipRef}>
            <div 
                ref={triggerRef}
                onClick={toggleText}
                aria-label="Mostrar información"
                className="text-turquesa hover:opacity-90 transition-colors focus:outline-none cursor-pointer"
            >
                <FontAwesomeIcon 
                    icon={faCircleInfo} 
                    className="text-md ml-1" 
                />
            </div>
            
            {showText && ReactDOM.createPortal(
                <div 
                    className={`
                        fixed
                        w-80 max-w-[90vw]
                        bg-white dark:bg-gray-700
                        text-gray-800 dark:text-gray-100
                        text-sm leading-normal
                        p-3 rounded-lg shadow-xl
                        border border-gray-200 dark:border-gray-600
                        z-[9999]
                        before:content-[''] before:absolute before:top-1/2 before:-translate-y-1/2
                        before:w-0 before:h-0 before:border-8 before:border-transparent
                        ${position === 'right' ? 
                            'before:-left-2 before:border-r-white dark:before:border-r-gray-700' : 
                            'before:-right-2 before:border-l-white dark:before:border-l-gray-700'}
                        animate-fade-in
                        whitespace-normal
                    `}
                    style={{
                        left: position === 'right' ? `${triggerRect?.right + 8}px` : 'auto',
                        right: position === 'left' ? `${window.innerWidth - (triggerRect?.left || 0) + 8}px` : 'auto',
                        top: `${(triggerRect?.top || 0) + (triggerRect?.height || 0) / 2}px`,
                        transform: 'translateY(-50%)'
                    }}
                >
                    {text}
                </div>,
                document.body
            )}
        </span>
    );
};

export default InfoTooltip;